import { isEmpty } from "lodash";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useReactFlow } from "reactflow";
import {
  useCreateExternalTransport,
  useGetExternalTransportById,
  useUpdateExternalTransport
} from "../../../../../../api/general/transport";
import {
  useCreateExternalTransport as useCreateSupplierExternalTransport,
  useGetExternalTransportById as useGetSupplierExternalTransportById,
  useUpdateExternalTransport as useUpdateSupplierExternalTransport
} from "../../../../../../api/general/supplierTransport";
import {
  useCreateSimulationExternalTransport,
  useGetSimulationExternalTransportById,
  useUpdateSimulationExternalTransport
} from "../../../../../../api/general/SimulationTransport";
import {
  useGetTransportationTypes
} from "../../../../../../api/static-data";
import { DrawingContext } from "../../../../../../components/drawing-tool/DrawingTool";
import { isNodeLabelExists } from "../../../../../../components/drawing-tool/utils";
import { MEASUREMENT_TYPES } from "../../../../../../config/enums/MeasurementTypes";
import { NodeLabelExistError } from "../../../../../../config/enums/NodeLabelError.enum";
import { Status } from "../../../../../../config/enums/StatusType.enum";
import { errorView, successMessage } from "../../../../../../helpers/ErrorHandler";
import { hasValidationErrors } from "../../../../../../utils/utils";
import { DrawingForIngredientContext } from "../../../../../supplier_drawing_tool/DrawingToolForIngredient";
import {
  ExternalTransportFormValues,
  ExternalTransportModalData,
  ExternalTransportToolVariant,
  SaveDocumentDataHandle
} from "../externalTransport.types";
import {
  mapTransportationTypesToOptions,
  mergeRetiredTransportType
} from "../externalTransport.utils";

interface UseExternalTransportBaseScreenOptions {
  modalData: ExternalTransportModalData;
  variant?: ExternalTransportToolVariant;
}

export const useExternalTransportBaseScreen = ({
  modalData,
  variant = "mapping"
}: UseExternalTransportBaseScreenOptions) => {
  const mappingContext = useContext(DrawingContext);
  const supplierContext = useContext(DrawingForIngredientContext);

  const drawingContext = variant === "supplier" ? supplierContext : mappingContext;

  const {
    nodeItemId,
    lockStatus = false,
    setNodeItem,
    setChartNodes,
    saveDrawing,
    chartEdges,
    chartNodes
  } = drawingContext as typeof mappingContext;

  const ownerId = variant === "mapping" || variant === "simulate" ? modalData.ownerId : undefined;
  const sourceId = variant === "mapping" || variant === "simulate" ? modalData.sourceId : undefined;
  const supplierId = variant === "supplier" ? modalData.supplierId : undefined;
  const supplierIngredientId =
    variant === "supplier" ? modalData.rawMaterialId ?? supplierContext.supplierIngredientId : undefined;

  const isDisconnected = modalData.isDisconnected ?? false;
  const { nodeItem } = modalData;
  const notesRef = useRef<SaveDocumentDataHandle | null>(null);
  const { deleteElements, getEdges } = useReactFlow();

  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);

  const externalTransportId = nodeItem?.data?.reference?.externalTransportId || null;
  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const useGetById =
    variant === "supplier"
      ? useGetSupplierExternalTransportById
      : variant === "simulate"
        ? useGetSimulationExternalTransportById
        : useGetExternalTransportById;

  const useCreate =
    variant === "supplier"
      ? useCreateSupplierExternalTransport
      : variant === "simulate"
        ? useCreateSimulationExternalTransport
        : useCreateExternalTransport;

  const useUpdate =
    variant === "supplier"
      ? useUpdateSupplierExternalTransport
      : variant === "simulate"
        ? useUpdateSimulationExternalTransport
        : useUpdateExternalTransport;

  const { data, status, fetchStatus } = useGetById(externalTransportId);
  const { isLoading: createLoading, mutate: createExternalTransport } = useCreate();
  const { isLoading: updateLoading, mutate: updateExternalTransport } = useUpdate();

  const {
    data: transportationTypesData,
    isSuccess: transportationTypesSuccess,
    isError: transportationTypesError,
    isFetching: transportationTypesFetching,
    refetch: refetchTransportationTypes
  } = useGetTransportationTypes();

  const existingTransportNames = useMemo(() => {
    if (status === "success") {
      return data?.externalTransportName ?? "";
    }
    return "";
  }, [status, data?.externalTransportName]);

  const transportationTypes = useMemo(() => {
    if (!transportationTypesSuccess) {
      return undefined;
    }
    const options = mapTransportationTypesToOptions(transportationTypesData);
    return mergeRetiredTransportType(options, data?.transportationType);
  }, [transportationTypesData, transportationTypesSuccess, data?.transportationType]);

  const formValues = useMemo<ExternalTransportFormValues>(
    () => ({
      externalTransportName: data?.externalTransportName || "",
      from: data?.from || "",
      to: data?.to || "",
      weight: data?.weight || 0,
      distance: data?.distance || 0,
      transportationType: data?.transportationType?.toString() || "1",
      weightUnit: data?.weightUnit || MEASUREMENT_TYPES.WEIGHT_KG
    }),
    [data]
  );

  const form = useForm<ExternalTransportFormValues>({
    defaultValues: formValues,
    values: formValues
  });

  const { control, handleSubmit, getValues, setFocus, setError, clearErrors, formState } = form;
  const { errors } = formState;

  const isFormDisabled = lockStatus || isDisconnected;
  const saveDisabledByTypes = transportationTypesError;
  const hasFormErrors = hasValidationErrors(errors);
  const saveDisabled =
    hasFormErrors ||
    lockStatus ||
    isDisconnected ||
    saveDisabledByTypes;

  const supportsNotes = variant === "mapping";

  const saveTransportNodeData = useCallback(
    async (id: string, externalTransportName: string) => {
      try {
        if (supportsNotes) {
          await notesRef.current?.saveDocumentAndNote(id);
        }
        const updatedNode = {
          ...nodeItem,
          data: {
            ...nodeItem?.data,
            label: `External Transport (${externalTransportName})`,
            reference: {
              externalTransportId: id
            },
            description: "External Transport"
          }
        };
        const updatedChartNodes = chartNodes.map((node: any) =>
          node.id === nodeItem?.id ? updatedNode : node
        );
        setChartNodes(updatedChartNodes);
        setNodeItem(updatedNode);
        saveDrawing(chartEdges, updatedChartNodes);
      } catch (error) {
        errorView(error);
      }
    },
    [
      supportsNotes,
      nodeItem,
      chartNodes,
      setChartNodes,
      setNodeItem,
      saveDrawing,
      chartEdges
    ]
  );

  const onNodesDelete = useCallback(() => {
    notesRef.current?.deleteComment();
    deleteElements({ nodes: [{ id: nodeItem.id }], edges: connectedEdges });
    setNodeItem(null);
  }, [connectedEdges, deleteElements, nodeItem, setNodeItem]);

  const onSubmit = useCallback(
    (formData: ExternalTransportFormValues) => {
      const transportNameChanged = getValues("externalTransportName");
      if (
        existingTransportNames !== transportNameChanged &&
        isNodeLabelExists(chartNodes as any, transportNameChanged)
      ) {
        clearErrors("externalTransportName");
        setFocus("externalTransportName");
        setError("externalTransportName", {
          type: "manual",
          message: NodeLabelExistError.EXTERNAL_TRANSPORT
        });
        return;
      }

      if (isEmpty(formData.transportationType)) {
        errorView("Please select transportation type");
        return;
      }

      const payload: Record<string, unknown> = {
        ...formData,
        weight: Number(formData.weight),
        distance: Number(formData.distance),
        transportationType: Number(formData.transportationType),
        weightUnit: Number(formData.weightUnit)
      };

      if (variant === "supplier") {
        if (!supplierIngredientId || !supplierId) {
          errorView("Please connect ingredient before create external transport");
          return;
        }
        payload.ingredientId = supplierIngredientId;
        payload.supplierId = supplierId;
      } else if (ownerId && sourceId) {
        payload.ownerId = ownerId;
        payload.sourceId = sourceId;
        if (variant === "simulate") {
          payload.changeStatus = true;
        }
      } else {
        errorView("Please connect ingredient before create external transport");
        return;
      }

      if (externalTransportId) {
        (updateExternalTransport as any)(
          { id: externalTransportId, data: payload },
          {
            onSuccess: (response: any) => {
              saveTransportNodeData(response?._id, response?.externalTransportName);
              successMessage("External transport updated successfully");
            }
          }
        );
        return;
      }

      (createExternalTransport as any)(payload, {
        onSuccess: (response: any) => {
          saveTransportNodeData(response?._id, response?.externalTransportName);
          successMessage("External transport created successfully");
        }
      });
    },
    [
      getValues,
      existingTransportNames,
      chartNodes,
      clearErrors,
      setFocus,
      setError,
      variant,
      supplierIngredientId,
      supplierId,
      ownerId,
      sourceId,
      externalTransportId,
      updateExternalTransport,
      saveTransportNodeData,
      createExternalTransport
    ]
  );

  const handleDelete = useCallback(() => {
    if (externalTransportId) {
      (updateExternalTransport as any)(
        {
          id: externalTransportId,
          data: {
            ...data,
            status: Status.DISABLE
          }
        },
        {
          onSuccess: () => {
            onNodesDelete();
            setVisibleDeleteModal(false);
            successMessage("External Transport deleted successfully");
          }
        }
      );
      return;
    }
    onNodesDelete();
    setVisibleDeleteModal(false);
    successMessage("External Transport deleted successfully");
  }, [data, externalTransportId, onNodesDelete, updateExternalTransport]);

  const isPageLoading =
    (status === "loading" && fetchStatus === "fetching") || createLoading || updateLoading;

  return {
    variant,
    isPageLoading,
    isFormDisabled,
    saveDisabled,
    supportsNotes,
    control,
    form,
    handleSubmit,
    onSubmit,
    notesRef,
    externalTransportId,
    transportationTypes,
    transportationTypesFetching,
    transportationTypesError,
    refetchTransportationTypes,
    weightDisabled: variant === "supplier",
    distanceDisabled: variant === "supplier",
    visibleDeleteModal,
    setVisibleDeleteModal,
    handleDelete,
    lockStatus,
    deleteDisabled: lockStatus,
    saveTitle: externalTransportId ? "Update Transport" : "Save Transport"
  };
};
